import type { ReactNode } from 'react';
import { Box, Flex, SimpleGrid, Skeleton, Spinner, Stack, Text } from '@chakra-ui/react';
import { Button, Card } from '../../../ui';
import type { InventoryCategory } from '../../../types/inventory';
import { PartNumberCard } from './PartSummaryCard';

interface LookingUpStepProps {
    partNumber: string;
    category: InventoryCategory;
    onChange: () => void;
    onSkip: () => void;
}

const SKELETON_COLORS = { startColor: 'bg.subtle', endColor: 'border.subtle' } as const;

const FieldSkeleton = ({ labelWidth = 14 }: { labelWidth?: number }) => (
    <Box>
        <Skeleton {...SKELETON_COLORS} h={2.5} w={labelWidth} borderRadius="full" mb={2} />
        <Skeleton {...SKELETON_COLORS} h={8} borderRadius="lg" />
    </Box>
);

/** Mirrors a TabbedSections box so the form doesn't jump when it loads. */
const SectionSkeleton = ({ title, children }: { title: string; children: ReactNode }) => (
    <Card variant="outline" bg="transparent" p={0}>
        <Text fontSize="md" fontWeight="semibold" color="fg.default" px={4} py={3}>
            {title}
        </Text>
        <Box px={4} py={4} borderTopWidth="1px" borderColor="border.subtle">
            {children}
        </Box>
    </Card>
);

const LookingUpStep = ({ partNumber, category, onChange, onSkip }: LookingUpStepProps) => (
    <Stack spacing={6} py={5}>
        <PartNumberCard partNumber={partNumber} category={category} onChange={onChange} />

        <Card variant="outline" role="status" aria-live="polite" px={4} py={3.5}>
            <Flex align="center" gap={3.5}>
                <Spinner
                    size="lg"
                    thickness="3px"
                    speed="0.8s"
                    color="brand.solid"
                    emptyColor="brand.subtle"
                    flexShrink={0}
                />
                <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                        Checking the parts catalog…
                    </Text>
                    <Text textStyle="caption" color="fg.muted" mt={0.5}>
                        Then your inventory for an existing match. Usually a few seconds.
                    </Text>
                </Box>
                <Button size="sm" variant="outlineGray" flexShrink={0} onClick={onSkip}>
                    Skip
                </Button>
            </Flex>
        </Card>

        <Stack spacing={4} aria-hidden opacity={0.7}>
            <SectionSkeleton title="Part Details">
                <Stack spacing={3}>
                    <FieldSkeleton labelWidth={12} />
                    <FieldSkeleton labelWidth={16} />
                    <FieldSkeleton labelWidth={14} />
                </Stack>
            </SectionSkeleton>
            <SectionSkeleton title="Where It Lives">
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                    <FieldSkeleton />
                    <FieldSkeleton labelWidth={8} />
                    <FieldSkeleton labelWidth={20} />
                    <FieldSkeleton labelWidth={8} />
                </SimpleGrid>
            </SectionSkeleton>
            <SectionSkeleton title="Cost And Restocking">
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                    <FieldSkeleton labelWidth={14} />
                    <FieldSkeleton labelWidth={20} />
                </SimpleGrid>
            </SectionSkeleton>
        </Stack>
    </Stack>
);

export default LookingUpStep;
